import React from "react";
import { useForm, Controller } from "react-hook-form";
import type { UserInfo } from "../types/notice";
import {
  validateEmail,
  validateName,
  validateCompanyName,
  validatePhoneNumber,
  sanitizeInput,
} from "../utils/validation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface UserInfoFormProps {
  onSubmit: (userInfo: UserInfo) => void;
  initialValues?: Partial<UserInfo>;
  isSubmitting?: boolean;
  className?: string;
}

interface FormData extends UserInfo {
  consent: boolean;
}

export const UserInfoForm: React.FC<UserInfoFormProps> = ({
  onSubmit,
  initialValues = {},
  isSubmitting = false,
  className = "",
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      user_email: initialValues.user_email || "",
      first_name: initialValues.first_name || "",
      last_name: initialValues.last_name || "",
      company_name: initialValues.company_name || "",
      phone_number: initialValues.phone_number || "",
      consent: false,
    },
  });

  const emailValue = watch("user_email");
  const isEmailValid = emailValue ? validateEmail(emailValue) : false;

  const onFormSubmit = (data: FormData) => {
    const sanitizedData: UserInfo = {
      user_email: sanitizeInput(data.user_email),
      first_name: sanitizeInput(data.first_name),
      last_name: sanitizeInput(data.last_name),
      company_name: sanitizeInput(data.company_name),
      phone_number: sanitizeInput(data.phone_number),
    };
    onSubmit(sanitizedData);
  };

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className={className}
    >
      {/* Divider */}
      <div className="border-t border-border pt-6 mb-6">
        <p className="text-sm text-muted-foreground">
          Where should we send your results?
        </p>
      </div>

      {/* Email */}
      <div className="space-y-2 mb-5">
        <Label htmlFor="user_email">Email</Label>
        <Input
          id="user_email"
          type="email"
          placeholder="you@company.com"
          disabled={isSubmitting}
          aria-invalid={!!errors.user_email}
          {...register("user_email", {
            required: "Email is required",
            validate: (value) =>
              validateEmail(value) || "Enter a valid email address",
          })}
        />
        {errors.user_email && (
          <p className="text-xs text-destructive">{errors.user_email.message}</p>
        )}
      </div>

      {/* Progressive disclosure */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden p-1 -m-1 ${
          isEmailValid ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {/* Name row */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="space-y-2">
            <Label htmlFor="first_name">First name</Label>
            <Input
              id="first_name"
              type="text"
              placeholder="Jane"
              disabled={isSubmitting}
              aria-invalid={!!errors.first_name}
              {...register("first_name", {
                required: "First name is required",
                validate: (value) =>
                  validateName(value) || "Enter a valid name",
              })}
            />
            {errors.first_name && (
              <p className="text-xs text-destructive">{errors.first_name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name">Last name</Label>
            <Input
              id="last_name"
              type="text"
              placeholder="Smith"
              disabled={isSubmitting}
              aria-invalid={!!errors.last_name}
              {...register("last_name", {
                required: "Last name is required",
                validate: (value) =>
                  validateName(value) || "Enter a valid name",
              })}
            />
            {errors.last_name && (
              <p className="text-xs text-destructive">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        {/* Company */}
        <div className="space-y-2 mb-5">
          <Label htmlFor="company_name">Company</Label>
          <Input
            id="company_name"
            type="text"
            placeholder="Acme Inc."
            disabled={isSubmitting}
            aria-invalid={!!errors.company_name}
            {...register("company_name", {
              required: "Company name is required",
              validate: (value) =>
                validateCompanyName(value) || "Enter a valid company name",
            })}
          />
          {errors.company_name && (
            <p className="text-xs text-destructive">{errors.company_name.message}</p>
          )}
        </div>

        {/* Phone (optional) */}
        <div className="space-y-2 mb-6">
          <Label htmlFor="phone_number" className="text-muted-foreground">
            Phone <span className="text-xs font-normal">(optional)</span>
          </Label>
          <Input
            id="phone_number"
            type="tel"
            placeholder="(555) 123-4567"
            disabled={isSubmitting}
            aria-invalid={!!errors.phone_number}
            {...register("phone_number", {
              validate: (value) =>
                !value || value.trim() === "" || validatePhoneNumber(value) ||
                "Enter a valid US phone number",
            })}
          />
          {errors.phone_number && (
            <p className="text-xs text-destructive">{errors.phone_number.message}</p>
          )}
        </div>

        {/* Consent */}
        <div className="mb-6">
          <Controller
            name="consent"
            control={control}
            rules={{ required: "You must consent to proceed" }}
            render={({ field }) => (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                  aria-invalid={!!errors.consent}
                  className="mt-0.5"
                />
                <Label
                  htmlFor="consent"
                  className="text-xs leading-relaxed text-muted-foreground font-normal cursor-pointer"
                >
                  I consent to DianaHR processing the document and the information I provide to generate a summary, recommend next steps, and contact me about the results.
                </Label>
              </div>
            )}
          />
          {errors.consent && (
            <p className="text-xs text-destructive mt-2">{errors.consent.message}</p>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="w-full"
          size="lg"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
              Analyzing...
            </span>
          ) : (
            "Analyze Notice"
          )}
        </Button>
      </div>
    </form>
  );
};
